import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Spinner,
} from "react-bootstrap";
import { useToast } from "../../components/ToastHost";
import { getLiveLanguages, languages } from "../../models/company";
import { MailTemplatePath } from "../../models/mail-template";
import { useTemplateTestFields } from "../../hook/template-test-fields";
import { useAppSelector } from "../../store";
import { renderMailTemplate } from "../../utils/http/mail-templates";

/**
 * Shows the actual rendered mail, as produced by the services render endpoint (the same
 * render the send path uses, minus tracking and test decoration).
 *
 * The document is put in an iframe so the mail's own CSS cannot leak into Watchtower --
 * mail templates ship full documents with their own resets. The iframe is then grown to
 * the document's height so the preview never scrolls inside its own frame.
 */
function TemplatePreview({
  mailTemplatePath,
  translationStatus = "approved",
}: {
  mailTemplatePath: MailTemplatePath;
  // Which translation-key values t("key") calls resolve against.
  translationStatus?: "draft" | "approved";
}) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [height, setHeight] = useState(0);

  // Which language to render in. Deliberately local state and not part of the URL: the
  // template itself is stored once (the path's language) and translated by Jinja, so this
  // picks a rendering of the same template rather than a different template.
  const activeCompany = useAppSelector((state) => state.company.activeCompany);
  const liveLanguages = getLiveLanguages(activeCompany?.info?.languages);
  const companyLanguages: string[] = liveLanguages.length ? liveLanguages : [mailTemplatePath.language as string];
  const [renderLanguage, setRenderLanguage] = useState<string>(
    mailTemplatePath.language as string,
  );

  // Falls back to the path's language when the company drops the selected one.
  const activeLanguage = companyLanguages.includes(renderLanguage)
    ? renderLanguage
    : (mailTemplatePath.language as string);

  // Render with exactly the fields Send Test Mail would use -- both read the same derived
  // value (the template's backend dataset plus the user's overrides), so the preview and
  // the test mail cannot show different content.
  const { automation, event, action, target } = mailTemplatePath;
  const templateKey =
    automation && event && action && target ? `${automation}.${event}.${action}.${target}` : null;
  const { fields: testFields } = useTemplateTestFields(templateKey);
  // The language toggle is nothing more than this field: the template is stored once and
  // Jinja translates off LANGUAGE. Set last so the toggle wins over a LANGUAGE that
  // happens to be in the test fields.
  const fields = useMemo(
    () => ({ ...testFields, LANGUAGE: activeLanguage }),
    [testFields, activeLanguage],
  );

  const {
    data: rendered,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ["mailTemplates", "render", mailTemplatePath, fields, translationStatus],
    queryFn: ({ signal }) =>
      renderMailTemplate({ signal, mailTemplatePath, fields, translationStatus }),
    retry: false,
    // The language toggle and the test fields are part of the key, so switching either
    // starts a fresh cache entry. Without this the preview would blank out on those two
    // while the new render is in flight -- keep the previous document up and dim it, the
    // same way a body edit (which reuses the key) already behaves.
    placeholderData: keepPreviousData,
  });

  const syncHeight = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc?.documentElement) return;
    // Height of the content itself: the iframe's own height is what we are setting, so
    // measure the document, not the frame.
    setHeight(
      Math.max(doc.documentElement.scrollHeight, doc.body?.scrollHeight ?? 0),
    );
  }, []);

  // The document keeps growing after load -- images arrive, web fonts swap in -- so watch
  // it rather than measuring once in onLoad.
  useEffect(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc?.body) return;

    const observer = new ResizeObserver(syncHeight);
    observer.observe(doc.documentElement);
    observer.observe(doc.body);
    return () => observer.disconnect();
  }, [rendered?.html, syncHeight]);

  const renderErrorMessage =
    (error as any)?.info?.message ?? (error as any)?.message;

  // Only worth a toast while a previous render is still on screen: that is the case the
  // user cannot otherwise see, because the preview looks fine but no longer matches the
  // editor. With no previous render there is a full Alert below saying the same thing.
  useToast(
    "mail-template-render",
    isError && rendered
      ? {
          title: "Preview not updating",
          body:
            "This template does not render at the moment, so the preview still shows the last version that did.",
          detail: renderErrorMessage,
          // An alert, not a warning: the preview on screen is actively misleading while
          // this is up, so it should carry the same weight as the red "Unsaved" marker.
          variant: "danger" as const,
        }
      : null,
  );

  // Rendered above every state below, including the error one -- losing the toggle
  // whenever a render fails would leave no way back to a language that does render.
  const header = (
    <div className="d-flex align-items-center justify-content-between mt-3">
      <h3 className="mb-0">Preview:</h3>
      {companyLanguages.length > 1 && (
        <ToggleButtonGroup
          type="radio"
          name="preview-language"
          value={activeLanguage}
          onChange={(value: string) => setRenderLanguage(value)}
          size="sm"
        >
          {companyLanguages.map((l) => (
            <ToggleButton
              key={`preview-language-${l}`}
              id={`preview-language-${l}`}
              value={l}
              variant="outline-secondary"
            >
              {languages.find((lang) => lang.id === l)?.name ?? l}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
    </div>
  );

  // Only for the very first render. Every draft save invalidates this query, so swapping
  // the preview out for a spinner on each refetch would make it flash while typing --
  // keep the previous document on screen and just dim it instead.
  if (isLoading) {
    return (
      <>
        {header}
        <div className="pt-3">
          <Spinner animation="border" size="sm" />{" "}
          <span className="ms-1">Rendering...</span>
        </div>
      </>
    );
  }

  // Only when there is nothing to fall back to. Once a render has succeeded, a later
  // failure keeps that document on screen (dimmed) and reports itself through the toast
  // above -- while typing, a template is briefly unparseable all the time, and swapping
  // the preview out for an error panel on every keystroke made it flash.
  if (isError && !rendered) {
    return (
      <>
        {header}
        <Alert className="alert alert-danger text-danger mt-3">
          Could not render this template: {renderErrorMessage}
        </Alert>
      </>
    );
  }

  // Stale for either reason: a render is in flight, or the last one failed and what is
  // on screen predates the current template.
  const dimmed = isFetching || isError;

  return (
    <>
      {header}

      {/* The rendered subject, labelled and set apart from the mail body -- it is part of
          the render, not part of the document in the iframe, so without this it would not
          be visible anywhere in the preview. */}
      <div
        className="mt-3 px-3 py-2 d-flex align-items-baseline"
        style={{
          backgroundColor: "#f1f3f5",
          border: "1px solid #dee2e6",
          borderRadius: "0.25rem",
          opacity: dimmed ? 0.5 : 1,
          transition: "opacity 0.15s",
        }}
      >
        <span
          className="text-muted text-uppercase me-2"
          style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}
        >
          Subject
        </span>
        <span style={{ fontWeight: 600 }}>
          {rendered?.subject || (
            <em className="text-muted fw-normal">(no subject)</em>
          )}
        </span>
      </div>

      <iframe
        ref={iframeRef}
        title="Rendered mail"
        srcDoc={rendered?.html ?? ""}
        onLoad={syncHeight}
        // allow-same-origin only: we need to read the document to size it, but the mail's
        // own scripts should not run in a preview.
        sandbox="allow-same-origin"
        scrolling="no"
        className="pt-3"
        style={{
          width: "100%",
          height: height ? `${height}px` : "0px",
          border: "none",
          overflow: "hidden",
          opacity: dimmed ? 0.5 : 1,
          transition: "opacity 0.15s",
        }}
      />
    </>
  );
}

export default TemplatePreview;
